import Button from '@/common/components/Button'
import { DateInput } from '@/common/components/DateInput/DateInput'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import { useGeosOptions } from '@/common/hooks/queries/useGeosOptions'
import useDebounce from '@/common/hooks/useDebounce'
import { GeoTransformers } from '@/common/services/geoTransformers'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { FormTransformers } from '@/common/utils/formTransformers'
import { CompanyContractType } from '@/features/privateCompanies/types/companyContract.type'
import { filterOptions } from '@/features/users/constants/filterOptions.constant'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FilterOptionOption } from 'react-select'
import { useUpdateContractGeneralInformation } from '../../hooks/contract/useUpdateContractGeneralInformation'
import {
  updateContractGeneralInformationSchema,
  UpdateContractGeneralInformationSchema,
} from '../../schemas/contract/updateContractGeneralInformation.schema'

interface UpdateContractGeneralInformationModalProps {
  contract?: CompanyContractType
}

export const UpdateContractGeneralInformationModal = ({ contract }: UpdateContractGeneralInformationModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [geoSearch, setGeoSearch] = useDebounce('')

  const geos = useGeosOptions(geoSearch)

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<UpdateContractGeneralInformationSchema>({
    resolver: zodResolver(updateContractGeneralInformationSchema),
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['clientContract', contract?.id] })
    setIsOpen(false)
  }

  const { mutate: updateContractGeneralInformation, isPending: updateContractGeneralInformationIsPending } =
    useUpdateContractGeneralInformation({
      contractId: contract?.id,
      contract,
      onSuccess,
      onError: FormTransformers.errorsTransformer<UpdateContractGeneralInformationSchema>({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<UpdateContractGeneralInformationSchema> = (data) => {
    updateContractGeneralInformation(data)
  }

  useEffect(() => {
    if (isOpen) {
      reset({
        contract: {
          in_effect_until: contract?.in_effect_until ? dateTransformer(contract.in_effect_until) : '',
          geo_geoables: GeoTransformers.mapToGeo()(contract?.geo_geoables),
        },
      })
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_contract_general_information_modal"
          operationKlassToCheckPermission={'Admin::Client::Contracts::UpdateContractGeneralInformation'}
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      title="Editando dados gerais do contrato"
      dataTestid="update_contract_general_information"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={updateContractGeneralInformationIsPending}
      confirmButtonIsLoading={updateContractGeneralInformationIsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <DateInput
              fieldName="contract.in_effect_until"
              control={control}
              errors={errors}
              errorMessage={errors.contract?.in_effect_until?.message || undefined}
              label="Data fim do contrato"
              markAsRequired
              disabled={updateContractGeneralInformationIsPending}
            />
            <SelectController
              control={control}
              name="contract.geo_geoables"
              label="Região de atuação"
              filterOption={
                filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean
              }
              defaultValue={GeoTransformers.mapToGeo()(contract?.geo_geoables)}
              options={GeoTransformers.mapToGeoOptions()(geos)}
              errorMessage={errors?.contract?.geo_geoables?.message || undefined}
              isMulti
              onInputChange={setGeoSearch}
              isDisabled={updateContractGeneralInformationIsPending}
            />
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
